import { CaretDownIcon } from "@/components/icons/caret-down-icon";
import { useGetBodyStats } from "@/hooks/requests/body-status/use-get-body-stats";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { cn } from "@/utils/cn";
import { IonSkeletonText } from "@ionic/react";
import { memo, useState } from "react";
import { ProfileStatusDetailsModal } from "../profile-status-details-modal";
import { Divider } from "./divider";
import { StatusItem } from "./status-item";

interface ProfileStatusCardProps {
  className?: string;
}

export const ProfileStatusCard = memo(function ProfileStatusCard({
  className,
}: ProfileStatusCardProps) {
  const { user, isLoading: isUserLoading } = useGetUser();
  const { bodyStats, isLoading: isBodyStatsLoading } = useGetBodyStats();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"joined" | "started" | "current" | null>(null);

  const isLoading = isUserLoading || isBodyStatsLoading;
  if (isLoading) {
    return (
      <IonSkeletonText animated style={{ width: "100%", height: "96px", borderRadius: "32px" }} />
    );
  }

  const startedAtWeight = bodyStats?.startingWeight ? Number(bodyStats.startingWeight) : 0;
  const startedAtWeightUnit = bodyStats?.startingWeightUnit || "kg";
  const currentWeight = bodyStats?.currentWeight ? Number(bodyStats.currentWeight) : 0;
  const currentWeightUnit = bodyStats?.currentWeightUnit || "kg";
  const joinedAt = user?.personalInfo?.creationDate
    ? new Date(user.personalInfo.creationDate)
    : undefined;
  const joinedDays = joinedAt
    ? Math.floor((new Date().getTime() - new Date(joinedAt).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  const handleStatusClick = (type: "joined" | "started" | "current") => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleModalDismiss = () => {
    setIsModalOpen(false);
    setModalType(null);
  };

  return (
    <>
      <div
        className={cn(
          "border-gray-theme h-24 w-full rounded-4xl border bg-[#fdfefe] p-4",
          "flex justify-around",
          "shadow-[0px_8px_32.35px_rgba(64,0,255,0.1),inset_0px_-12px_6px_0px_rgba(98,10,213,0.06)]",
          className,
        )}
      >
        <StatusItem
          label="On plan"
          value={joinedDays.toString()}
          unit={joinedDays > 1 ? "days" : "day"}
          onClick={() => handleStatusClick("joined")}
          icon={<CaretDownIcon />}
        />

        <Divider orientation="vertical" />

        <StatusItem
          label="Started at"
          value={startedAtWeight.toString()}
          unit={startedAtWeightUnit}
        />

        <Divider orientation="vertical" />

        <StatusItem label="Current" value={currentWeight.toString()} unit={currentWeightUnit} />
      </div>

      <ProfileStatusDetailsModal
        isOpen={isModalOpen}
        onDismiss={handleModalDismiss}
        type={modalType}
        joinedDate={joinedAt}
        joinedDays={joinedDays}
        startedWeight={startedAtWeight}
        startedWeightUnit={startedAtWeightUnit}
        currentWeight={currentWeight}
        currentWeightUnit={currentWeightUnit}
      />
    </>
  );
});
